import type { User } from '../types/auth';

/** Canonical CA firm membership roles (owner > admin > member > viewer). */
export type CAFirmRole = 'owner' | 'admin' | 'member' | 'viewer';

const OWNER_ALIASES = ['owner', 'firm_owner', 'ca_owner'] as const;
const ADMIN_ALIASES = ['admin', 'ca_admin', 'manager', 'primary_admin'] as const;
const MEMBER_ALIASES = ['member', 'associate', 'staff', 'analyst'] as const;
const VIEWER_ALIASES = ['viewer', 'read_only', 'readonly'] as const;

export function normalizeCAFirmRole(role: string | null | undefined): CAFirmRole {
  const normalized = (role ?? '').trim().toLowerCase();
  if (OWNER_ALIASES.includes(normalized as (typeof OWNER_ALIASES)[number])) return 'owner';
  if (ADMIN_ALIASES.includes(normalized as (typeof ADMIN_ALIASES)[number])) return 'admin';
  if (MEMBER_ALIASES.includes(normalized as (typeof MEMBER_ALIASES)[number])) return 'member';
  if (VIEWER_ALIASES.includes(normalized as (typeof VIEWER_ALIASES)[number])) return 'viewer';
  return 'viewer';
}

/** Owner and admin can manage firm staff, invites, and firm-level settings. */
export function hasCAFirmAdminRole(role: CAFirmRole): boolean {
  return role === 'owner' || role === 'admin';
}

export function getCAFirmMembership(
  user: Pick<User, 'ca_firm_memberships'> | null | undefined,
  firmId?: string | null
) {
  const rawMemberships = user?.ca_firm_memberships;
  const memberships = Array.isArray(rawMemberships) ? rawMemberships : [];
  if (memberships.length === 0) return null;

  const trimmedFirmId = firmId?.trim();
  if (trimmedFirmId) {
    const match = memberships.find((m) => {
      const id = String(m.ca_firm_id ?? '').trim();
      return id === trimmedFirmId;
    });
    if (match) return match;
  }

  return (
    memberships.find((m) => m.is_active || (m as { active?: boolean }).active === true) ??
    memberships[0] ??
    null
  );
}

export function getActiveCAFirmRole(
  user: Pick<User, 'ca_firm_memberships'> | null | undefined,
  firmId?: string | null
): CAFirmRole {
  const membership = getCAFirmMembership(user, firmId);
  return normalizeCAFirmRole(membership?.role);
}
